import React, { useEffect } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { FaStar, FaShoppingCart } from "react-icons/fa";
import { useCart } from "../../hooks/CartContext";
import { useQuery } from "@tanstack/react-query";

const ProductCategoryPage = () => {
  const { categoryName } = useParams();
  
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();

  const isFilterPage = location.pathname === "/console";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categoryName]);

  const title = categoryName ? decodeURIComponent(categoryName) : "Our Products";

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["brand-products", categoryName],
    queryFn: async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/products/search?q=${encodeURIComponent(
          categoryName
        )}`
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Something went wrong fetching products");
      console.log("Fetched products:", json); // Debugging log  
      return json;
    },
    enabled: !!categoryName, 
  });

  // Updated to extract the array from the { products: [...] } response structure
  const products = data?.products || (Array.isArray(data) ? data : []);

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-red"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <div className="text-center">
          <p className="font-semibold text-red">Failed to load products</p>
          <p className="text-sm text-gray-500 mt-1">{error?.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
          {title}
        </h1>

        {!isFilterPage && (
          <Link
            to="/console"
            className="inline-flex items-center justify-center rounded-xl bg-red px-5 py-2.5 font-semibold text-white transition hover:opacity-90"
          >
            Filter
          </Link>
        )}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 sm:gap-5 md:grid-cols-3 lg:grid-cols-4">
        {products.map((p) => {
          const soldOut = Number(p?.quantity || 0) < 1;
          const hasDiscount = Number(p?.price || 0) > Number(p?.finalPrice || 0);

          return (
            <div
              key={p?._id}
              onClick={() => navigate(`/product/${p?._id}`)}
              // CHANGED: Added hover:scale-105, hover:z-10, hover:shadow-2xl and relative positioning
              className="group overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-2xl hover:scale-105 hover:z-10 cursor-pointer transition-all duration-300 relative"
            >
              {/* Image */}
              <div className="relative overflow-hidden">
                <img
                  src={p?.images?.[0] || "/placeholder.png"}
                  alt={p?.name || "Product"}
                  className="h-40 w-full bg-gray-100 object-cover sm:h-48"
                />

                <div className="absolute left-3 top-3 flex flex-wrap gap-2">
                  {p?.condition && (
                    <span className="rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-gray-700 shadow-sm">
                      {p.condition}
                    </span>
                  )}

                  {soldOut ? (
                    <span className="rounded-full bg-gray-900/90 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
                      Sold Out
                    </span>
                  ) : (
                    <span className="rounded-full bg-red px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
                      In Stock
                    </span>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="p-4 relative bg-white">
                {/* Name */}
                <div className="min-w-0">
                  {/* CHANGED: Added transition-colors and group-hover:text-red */}
                  <h2 className="truncate text-sm font-semibold text-gray-900 sm:text-base transition-colors group-hover:text-red">
                    {p?.name}
                  </h2>

                  <div className="mt-1 flex flex-wrap gap-2">
                    {p?.category && (
                      <span className="text-xs text-gray-500">
                        {p.category}
                      </span>
                    )}
                    {p?.platform && (
                      <span className="text-xs text-gray-500">
                        • {p.platform}
                      </span>
                    )}
                  </div>
                </div>

                {/* Brand */}
                {p?.brand && (
                  <p className="mt-2 truncate text-xs font-medium text-gray-600">
                    {p.brand}
                  </p>
                )}

                {/* Rating + Stock */}
                <div className="mt-3 flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1 font-bold text-yellow-500">
                    <FaStar />
                    {Number(p?.averageRating || 0).toFixed(1)}
                  </span>
                </div>

                {/* Price */}
                <div className="mt-3 flex items-end justify-between gap-2">
                  <p className="text-base font-bold text-gray-900 sm:text-lg">
                    ₦{Number(p?.finalPrice || 0).toLocaleString()}
                  </p>
                  {p?.discount > 0 && (
                    <>
                      {hasDiscount && (
                        <p className="text-sm font-semibold text-gray-400 line-through">
                          ₦{Number(p?.price || 0).toLocaleString()}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* CTA */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (soldOut) return;
                    addToCart(p, 1);
                  }}
                  disabled={soldOut}
                  // CHANGED: Enhanced button states with active:scale-95 and dynamic shadows
                  className={`mt-4 flex w-full items-center justify-center gap-2 rounded-xl py-2.5 font-semibold transition-all duration-300 active:scale-95 ${
                    soldOut
                      ? "cursor-not-allowed bg-gray-200 text-gray-600"
                      : "bg-red text-white hover:bg-red/90 shadow-md hover:shadow-lg"
                  }`}
                >
                  <FaShoppingCart />
                  {soldOut ? "Sold Out" : "Add to Cart"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {products.length === 0 && !isLoading && (
        <div className="rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
          <p className="font-semibold text-gray-900">No products found</p>
          <p className="mt-1 text-sm text-gray-500">
            Try another filter or search.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductCategoryPage;